import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import WingFlatPicker from '../components/vms/WingFlatPicker';
import { labelCls, errorCls, hintCls, controlCls } from '../components/form/formStyles';
import { DELIVERY_TYPES, DELIVERY_STATUSES, toOptions } from '../vmsConstants';
import { normalizePhone, isValidPhone } from '../visitorConstants';

const TYPE_OPTIONS = toOptions(DELIVERY_TYPES);
const STATUS_OPTIONS = toOptions(DELIVERY_STATUSES);

const EMPTY = {
    wing_id: '',
    flat_id: '',
    delivery_type: 'PARCEL',
    provider_name: '',
    delivery_person_name: '',
    delivery_person_phone: '',
    tracking_number: '',
    status: 'AT_GATE',
    entry_gate: '',
    photo_url: '',
    notes: '',
};

// Logging a parcel is a gate-desk action taken in seconds, so it happens in a
// modal over the list rather than on its own page.
//
// On edit the status field appears: the update endpoint is the only way to
// reach HELD_AT_GATE, REJECTED, RETURNED or CANCELLED — the complete endpoint
// accepts only the two delivered states.
const DeliveryFormModal = ({ open, societyId, initial, mode = 'create', submitting, onSubmit, onCancel }) => {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            setForm({ ...EMPTY, ...(initial || {}) });
            setErrors({});
        }
    }, [open, initial]);

    if (!open) return null;

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        setErrors(e => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (mode === 'create') {
            if (!form.wing_id) e.wing_id = 'Wing is required';
            if (!form.flat_id) e.flat_id = 'Flat is required';
        }
        if (!form.delivery_type) e.delivery_type = 'Delivery type is required';
        if (form.delivery_person_phone.trim() && !isValidPhone(form.delivery_person_phone)) {
            e.delivery_person_phone = 'Enter a 10-digit number, or a full number with country code';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = (ev) => {
        ev.preventDefault();
        if (!validate()) return;

        const payload = { delivery_type: form.delivery_type, status: form.status };
        [['provider_name', form.provider_name], ['delivery_person_name', form.delivery_person_name],
         ['tracking_number', form.tracking_number], ['entry_gate', form.entry_gate],
         ['photo_url', form.photo_url], ['notes', form.notes]]
            .forEach(([k, v]) => { if (v && v.trim()) payload[k] = v.trim(); });
        if (form.delivery_person_phone.trim()) {
            payload.delivery_person_phone = normalizePhone(form.delivery_person_phone);
        }
        // The flat a parcel is for is fixed at creation — the update endpoint
        // rejects these keys rather than ignoring them.
        if (mode === 'create') {
            payload.society_id = Number(societyId);
            payload.wing_id = Number(form.wing_id);
            payload.flat_id = Number(form.flat_id);
        }

        onSubmit(payload);
    };

    const unwrap = (v) => (v === 'all' ? '' : v);

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4 tw-overflow-y-auto">
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-2xl tw-my-8">
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h3 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-gray-800">
                            {mode === 'create' ? 'Log a delivery' : 'Edit delivery'}
                        </h3>
                        <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            {mode === 'create'
                                ? 'The flat’s residents are notified as soon as it is logged'
                                : initial?.delivery_code || ''}
                        </p>
                    </div>
                    <button onClick={onCancel} className="tw-p-1 tw-text-gray-400 hover:tw-text-gray-700">
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="tw-p-5 tw-space-y-4">
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                        {mode === 'create' ? (
                            <WingFlatPicker
                                societyId={societyId}
                                wingId={form.wing_id}
                                flatId={form.flat_id}
                                onWingChange={(v) => set('wing_id', v)}
                                onFlatChange={(v) => set('flat_id', v)}
                                errors={errors}
                            />
                        ) : (
                            <div className="sm:tw-col-span-2">
                                <label className={labelCls}>Flat</label>
                                <p className="tw-mb-0 tw-px-3 tw-py-2 tw-text-xs tw-text-gray-600 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-md">
                                    {initial?.flatLabel || '—'}
                                </p>
                            </div>
                        )}
                        <div>
                            <label className={labelCls}>Type <span className="tw-text-rose-500">*</span></label>
                            <SearchableDropdown
                                small
                                clearable={false}
                                value={form.delivery_type}
                                onChange={(v) => set('delivery_type', v)}
                                options={TYPE_OPTIONS}
                                placeholder="Select type"
                            />
                            {errors.delivery_type && <p className={errorCls}>{errors.delivery_type}</p>}
                        </div>
                    </div>

                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                        <div>
                            <label className={labelCls}>Provider</label>
                            <input
                                type="text"
                                value={form.provider_name}
                                onChange={e => set('provider_name', e.target.value)}
                                maxLength={150}
                                placeholder="e.g. Amazon, Swiggy"
                                className={controlCls(false)}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Delivery person</label>
                            <input
                                type="text"
                                value={form.delivery_person_name}
                                onChange={e => set('delivery_person_name', e.target.value)}
                                maxLength={150}
                                placeholder="optional"
                                className={controlCls(false)}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Their phone</label>
                            <input
                                type="tel"
                                inputMode="tel"
                                value={form.delivery_person_phone}
                                onChange={e => set('delivery_person_phone', e.target.value)}
                                maxLength={20}
                                placeholder="optional"
                                className={`${controlCls(!!errors.delivery_person_phone)} tw-font-mono`}
                            />
                            {errors.delivery_person_phone && <p className={errorCls}>{errors.delivery_person_phone}</p>}
                        </div>
                    </div>

                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                        <div>
                            <label className={labelCls}>Tracking number</label>
                            <input
                                type="text"
                                value={form.tracking_number}
                                onChange={e => set('tracking_number', e.target.value)}
                                maxLength={100}
                                placeholder="optional"
                                className={`${controlCls(false)} tw-font-mono`}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Entry gate</label>
                            <input
                                type="text"
                                value={form.entry_gate}
                                onChange={e => set('entry_gate', e.target.value)}
                                maxLength={50}
                                placeholder="e.g. Main Gate"
                                className={controlCls(false)}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Status</label>
                            <SearchableDropdown
                                small
                                clearable={false}
                                value={form.status}
                                onChange={(v) => set('status', unwrap(v) || 'AT_GATE')}
                                options={STATUS_OPTIONS}
                                placeholder="Select status"
                            />
                            {mode === 'edit' && (
                                <p className={hintCls}>Held, rejected and returned are set here</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className={labelCls}>Notes</label>
                        <textarea
                            rows={2}
                            value={form.notes}
                            onChange={e => set('notes', e.target.value)}
                            maxLength={1000}
                            placeholder="e.g. Left with the guard, resident not answering"
                            className={`${controlCls(false)} tw-resize-none`}
                        />
                    </div>

                    <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2 tw-border-t tw-border-gray-100">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md disabled:tw-opacity-50"
                        >
                            <Icon icon="mdi:package-variant-closed" className="tw-text-base" />
                            {submitting ? 'Saving...' : (mode === 'create' ? 'Log delivery' : 'Save changes')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DeliveryFormModal;
